import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { CITIES, CATEGORIES, CATEGORY_ICONS, FULL_ACTIVITIES, DISTRICTS, OTHER_HOME_ACTIVITY, SERVICE_METHODS } from "@/lib/constants";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import PhotoInput from "@/components/PhotoInput";
import PrivacyBanner from "@/components/PrivacyBanner";
import { toast } from "sonner";

const inputCls = "w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3";

export default function CreateRequest() {
  const { t, tCat, tAct, tCity } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    category: "", activity: "", other_detail: "", service_method: "", city: "", district: "", budget: "", deadline: "", description: "", photo: null,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const activities = FULL_ACTIVITIES[form.category] || [];
  const districts = DISTRICTS[form.city] || [];
  const needsActivity = activities.length > 0;
  const needsDistrict = districts.length > 0;
  const isOtherHome = form.category === "home_services" && form.activity === OTHER_HOME_ACTIVITY;
  const canSubmit = form.category && form.city && form.budget && form.deadline && form.description
    && (!needsActivity || form.activity) && (!needsDistrict || form.district)
    && (!isOtherHome || form.other_detail.trim());

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { ...form, budget: parseFloat(form.budget) };
      if (!isOtherHome) delete payload.other_detail;
      if (!payload.service_method) delete payload.service_method;
      const { data } = await api.post("/requests", payload);
      toast.success(t("requestPosted"));
      navigate(`/customer/request/${data.id}`);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-shell pb-24" data-testid="create-request-page">
      <TopBar title={t("newRequest")} />
      <form onSubmit={submit} className="px-5 pt-4 space-y-5">
        <PrivacyBanner variant="customer" />

        <div>
          <label className="label-caps mb-2 block">{t("category")}</label>
          {/* General banner — shown once above the categories */}
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-900 text-xs font-medium px-3 py-2.5 mb-3" data-testid="req-categories-banner">
            {t("categoriesBanner")}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICONS[c];
              const sel = form.category === c;
              return (
                <button
                  type="button"
                  key={c}
                  data-testid={`cat-${c}`}
                  onClick={() => setForm({ ...form, category: c, activity: "", other_detail: "" })}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition ${
                    sel ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />{tCat(c)}
                </button>
              );
            })}
          </div>
        </div>

        {form.category && needsActivity && (
          <div data-testid="activity-section">
            <label className="label-caps mb-2 block">{t("activity")}</label>
            {form.category === "pets" && (
              <p className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs font-medium px-3 py-2.5 mb-3" data-testid="vet-notice-request">
                {t("vetNotice")}
              </p>
            )}
            {form.category === "phones_electronics" && (
              <p className="rounded-xl bg-sky-50 border border-sky-200 text-sky-900 text-xs font-medium px-3 py-2.5 mb-3" data-testid="device-privacy-notice">
                {t("devicePrivacyNotice")}
              </p>
            )}
            <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto pe-1">
              {activities.map((a) => {
                const sel = form.activity === a;
                return (
                  <button
                    type="button"
                    key={a}
                    data-testid={`act-${a}`}
                    onClick={() => setForm({ ...form, activity: a, other_detail: "" })}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                      sel ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-300"
                    }`}
                  >
                    {tAct(a)}
                  </button>
                );
              })}
            </div>
            {isOtherHome && (
              <div className="mt-3" data-testid="other-home-section">
                <label htmlFor="other-home-detail" className="label-caps mb-1.5 block">{t("otherHomeQuestion")}</label>
                <input
                  id="other-home-detail"
                  data-testid="req-other-detail"
                  type="text"
                  required
                  value={form.other_detail}
                  onChange={(e) => setForm({ ...form, other_detail: e.target.value })}
                  className={inputCls}
                />
              </div>
            )}
          </div>
        )}

        <div>
          <label className="label-caps mb-2 block">{t("serviceMethod")} — {t("optional")}</label>
          <div className="flex flex-wrap gap-2" data-testid="service-method-section">
            {SERVICE_METHODS.map((m) => {
              const sel = form.service_method === m;
              return (
                <button
                  type="button"
                  key={m}
                  data-testid={`method-${m}`}
                  onClick={() => setForm({ ...form, service_method: sel ? "" : m })}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                    sel ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-300"
                  }`}
                >
                  {tAct(m)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps mb-1.5 block">{t("city")}</label>
            <select
              data-testid="req-city"
              required
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value, district: "" })}
              className={inputCls}
            >
              <option value="">{t("selectCity")}</option>
              {CITIES.map((c) => <option key={c} value={c}>{tCity(c)}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">{t("district")}</label>
            <select
              data-testid="req-district"
              required={needsDistrict}
              disabled={!needsDistrict}
              value={form.district}
              onChange={(e) => setForm({ ...form, district: e.target.value })}
              className={`${inputCls} disabled:opacity-50`}
            >
              <option value="">{t("selectDistrict")}</option>
              {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps mb-1.5 block">{t("budget")}</label>
            <input
              data-testid="req-budget"
              type="number"
              min="1"
              required
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">{t("deadline")}</label>
            <input
              data-testid="req-deadline"
              type="date"
              required
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="label-caps mb-1.5 block">{t("description")}</label>
          <textarea
            data-testid="req-description"
            required
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-2.5"
          />
        </div>

        <div>
          <label className="label-caps mb-2 block">{t("photoLabel")}</label>
          <PhotoInput value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} label={t("upload")} testId="req-photo" />
        </div>

        {err && <div data-testid="req-error" role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{err}</div>}

        <button data-testid="req-submit" disabled={busy || !canSubmit} className="btn-primary w-full">
          {busy ? t("loadingLabel") : t("submit")}
        </button>
      </form>
      <BottomNav />
    </div>
  );
}
